import { skillFor } from '../../ai/skills';
import { sameTactics, type Tactics } from '../../ai/tactics';
import { MATCH, STEP } from '../../game/constants';
import type { KnightSpec } from '../../game/match';
import type { MatchState, Role, TeamId } from '../../game/types';
import { clamp } from '../../game/vec';
import type { Manager, ManagerAction } from '../runner';
import { KEEPER_BUILD_VALUE, KEEPER_TIER_VALUE, OUTFIELD_BUILD_VALUE, OUTFIELD_TIER_VALUE } from './draft';
import { SQUAD_SIZE, STARTERS, type HouseStyle, type SquadKnight } from './types';

export const STAMINA_MAX = 100;
/** Tuned (and pinned in the tests) so a knight starting 10 of 14 with a rest every third stays at full
 * strength, 12 always tires him, and 14 straight leaves him near the floor for the run-in. */
export const PLAY_COST = 8;
export const REST_GAIN = 18;
export const FRESH = 70;
/** Speed at stamina 0. scripts/stamina.sim.ts, 600 Knight-tier matches a line: stamina 0 v fresh -0.64
 * ± 0.13 goals, stamina 35 -0.23 ± 0.13; a tier up +1.92 ± 0.15, down -0.64 ± 0.13: about half a tier. */
export const TIRED_FLOOR = 0.96;
/** Stamina 0 in tiers (measured above); line-ups and substitutions weigh tiredness with it. */
export const TIRED_TIER_COST = 0.5;

/** Stamina by squad knight id. */
export type Stamina = Readonly<Record<string, number>>;

export const freshStamina = (squad: readonly SquadKnight[]): Stamina =>
  Object.fromEntries(squad.map((k) => [k.id, STAMINA_MAX]));

const capped = (stamina: number): number => clamp(stamina, 0, STAMINA_MAX);

/** How far below FRESH, from 0 (fresh) to 1 (empty). */
const deficit = (stamina: number): number => Math.max(0, FRESH - capped(stamina)) / FRESH;

/** Multiplies a knight's speed: 1 at or above FRESH, falling in a straight line to TIRED_FLOOR at 0. */
export const staminaFactor = (stamina: number): number => 1 - (1 - TIRED_FLOOR) * deficit(stamina);

/** A squad knight as the match sees him: his tier's speed, slowed by tiredness. Bots play every knight. */
export const squadKnightSpec = (knight: SquadKnight, stamina: number, role: Role): KnightSpec => ({
  look: knight.look,
  role,
  human: false,
  tier: knight.tier,
  speedFactor: skillFor(knight.tier).speed * staminaFactor(stamina),
});

/** Expected strength in tiers, the scale every line-up and substitution choice is made on. */
const strength = (tier: number, stamina: number): number => tier - TIRED_TIER_COST * deficit(stamina);

const tiredCost = (stamina: number): number => TIRED_TIER_COST * deficit(stamina);

/** The one substitution of a match: who went off, who came on, and the simulation step it happened. */
export interface Substitution {
  readonly offId: string;
  readonly onId: string;
  readonly step: number;
}

export interface MatchdayPlay {
  /** The knights who started, by squad id. */
  readonly starters: readonly string[];
  readonly substitution: Substitution | null;
  /** Steps the match lasted (the runner's step at full time), so a sub's minutes are a share of it. */
  readonly matchSteps: number;
}

/** The share of the match gone at `step`. */
export function shareGone(step: number, matchSteps: number): number {
  if (!(matchSteps > 0) || !Number.isInteger(step) || step < 0 || step > matchSteps)
    throw new Error('A substitution must happen within the match');
  return step / matchSteps;
}

/** The share of the match each knight played, by squad id; knights who sat out are not listed. */
export function playingShares(play: MatchdayPlay): ReadonlyMap<string, number> {
  const shares = new Map(play.starters.map((id) => [id, 1]));
  if (shares.size !== STARTERS || play.starters.length !== STARTERS)
    throw new Error(`A line-up needs ${STARTERS} different starters`);
  const sub = play.substitution;
  if (sub) {
    if (!shares.has(sub.offId) || shares.has(sub.onId))
      throw new Error('A substitution takes a starter off for a knight from the bench');
    const gone = shareGone(sub.step, play.matchSteps);
    shares.set(sub.offId, gone);
    shares.set(sub.onId, 1 - gone);
  }
  return shares;
}

/** Everyone who played pays for the minutes they played; everyone else recovers. */
export function afterMatchday(stamina: Stamina, play: MatchdayPlay): Stamina {
  const shares = playingShares(play);
  for (const id of shares.keys()) if (!(id in stamina)) throw new Error(`${id} is not in the squad`);
  return Object.fromEntries(
    Object.entries(stamina).map(([id, s]) => {
      const share = shares.get(id);
      return [id, capped(share === undefined ? s + REST_GAIN : s - PLAY_COST * share)];
    }),
  );
}

export interface Starter {
  readonly knight: SquadKnight;
  readonly role: Role;
}

export interface Lineup {
  /** Keeper, defender, midfielder, forward. */
  readonly starters: readonly Starter[];
  /** Best substitute first. */
  readonly bench: readonly SquadKnight[];
}

/** Stamina spent now is missed in every later match played below FRESH, so it is priced per matchday
 * left. Star houses discount that future and lean on their stars; depth houses don't. */
const LOOKAHEAD: Readonly<Record<HouseStyle['draft'], number>> = { stars: 0.5, balanced: 0.75, depth: 1 };

const futureWeight = (style: HouseStyle, matchdaysLeft: number): number => {
  if (!Number.isInteger(matchdaysLeft) || matchdaysLeft < 1) throw new Error('No matchdays left to play');
  return (matchdaysLeft - 1) * LOOKAHEAD[style.draft];
};

/** Strength now, less what starting costs later compared with resting. */
const startValue = (knight: SquadKnight, stamina: number, weight: number): number =>
  strength(knight.tier, stamina) - weight * (tiredCost(stamina - PLAY_COST) - tiredCost(stamina + REST_GAIN));

const ROLES: readonly Role[] = ['gk', 'def', 'mid', 'fwd'];

/**
 * In goal, the starter the outfield would miss least: no keeper tier or build measured as worth anything,
 * while a star in goal cost his side about a goal a match. Ties go to the heavier build, then the rest
 * line up heaviest to lightest.
 */
function assignRoles(starters: readonly SquadKnight[], stamina: Stamina): Starter[] {
  const missed = (k: SquadKnight): number =>
    worth(k.tier, k.look.build, 'mid', staminaOf(stamina, k.id)) - worth(k.tier, k.look.build, 'gk', 0);
  const [keeper, ...rest] = [...starters].sort(
    (a, b) => missed(a) - missed(b) || b.look.build - a.look.build || a.id.localeCompare(b.id),
  );
  if (!keeper) throw new Error('No starters');
  const outfield = rest.sort(
    (a, b) => b.look.build - a.look.build || a.tier - b.tier || a.id.localeCompare(b.id),
  );
  return [keeper, ...outfield].map((knight, i) => ({ knight, role: ROLES[i] ?? 'mid' }));
}

function staminaOf(stamina: Stamina, id: string): number {
  const s = stamina[id];
  if (s === undefined || !Number.isFinite(s)) throw new Error(`No stamina for ${id}`);
  return s;
}

/** The four worth most once stamina is priced in: each knight's value is independent, so that is the
 * best total. Ties go to the fresher knight, so equal squads rotate themselves. */
export function chooseLineup(
  squad: readonly SquadKnight[],
  stamina: Stamina,
  style: HouseStyle,
  matchdaysLeft: number,
): Lineup {
  if (squad.length !== SQUAD_SIZE || new Set(squad.map((k) => k.id)).size !== SQUAD_SIZE)
    throw new Error(`A squad needs ${SQUAD_SIZE} different knights`);
  const weight = futureWeight(style, matchdaysLeft);
  const ranked = squad
    .map((knight) => ({ knight, s: staminaOf(stamina, knight.id) }))
    .map(({ knight, s }) => ({ knight, s, value: startValue(knight, s, weight) }))
    .sort((a, b) => b.value - a.value || b.s - a.s || a.knight.id.localeCompare(b.knight.id))
    .map((r) => r.knight);
  return { starters: assignRoles(ranked.slice(0, STARTERS), stamina), bench: ranked.slice(STARTERS) };
}

/** All in when chasing the game late: press high, push up, shoot on sight. */
export const CHARGE: Tactics = { press: 'high', shooting: 'early' };
/** Seeing it out: sit deep, press only in our half, keep the ball until the chance is clear. */
export const HOLD: Tactics = { press: 'low', shooting: 'patient' };

/** The earliest a rival considers his substitution: this far into the second half, or half-time when behind. */
export const SUB_AT = 2 / 3;
export const CHASING_SUB_AT = 1 / 2;
/** From here a rival behind charges and one ahead holds. */
export const LATE = 3 / 4;
/** The runner polls managers once a second; the poll count gives the step a sub happened on. */
const POLL_STEPS = Math.round(1 / STEP);

export interface BenchOption {
  readonly spec: KnightSpec;
  readonly stamina: number;
}

export interface RivalSetup {
  readonly style: HouseStyle;
  /** Stamina of the starters in line-up order, as their specs were given to the runner. */
  readonly starters: readonly number[];
  readonly bench: readonly BenchOption[];
  /** Including this one. */
  readonly matchdaysLeft: number;
}

/** The substitution a rival made: line-up slot off, bench slot on, and the step, for afterMatchday. */
export interface RivalSub {
  readonly offSlot: number;
  readonly benchSlot: number;
  readonly step: number;
}

export interface RivalManager {
  /** Give it to the runner from the kick-off, for a side that starts on `style.tactics`. */
  readonly manage: Manager;
  substitution(): RivalSub | null;
}

/** The share of the match gone by the clock, so stoppages never count toward a decision. */
const clockGone = (state: MatchState): number => clamp(1 - state.clock / MATCH.duration, 0, 1);

const leadOf = (state: MatchState, team: TeamId): number =>
  state.score[team] - state.score[team === 0 ? 1 : 0];

/** Late on, behind charges and ahead holds; otherwise the house plays its own way. */
export function rivalTactics(style: HouseStyle, gone: number, lead: number): Tactics {
  if (gone < LATE || lead === 0) return style.tactics;
  return lead < 0 ? CHARGE : HOLD;
}

interface OnPitch {
  readonly id: number;
  readonly tier: number;
  readonly build: number;
  readonly role: Role;
  readonly stamina: number;
}

/** One tier for one knight, in goals a match: the measured Peasant-to-Legend span over its four steps. */
const GOALS_PER_TIER =
  ((OUTFIELD_TIER_VALUE.at(-1) ?? 0) - (OUTFIELD_TIER_VALUE[0] ?? 0)) / (OUTFIELD_TIER_VALUE.length - 1);
/** One standard error of each fitted tier or build value, in goals ('draft values' in scripts/draft.sim.ts). */
const VALUE_SE = 0.06;

/**
 * A sub must still help at SUB_MARGIN_SE standard errors below its fitted gain. scripts/rivals.sim.ts, 12 real
 * seasons: rivals sub in 52% of matches (40–50% within a goal at the hour, 76% with a fitted-better bench); v the
 * same side with no subs -0.02 ± 0.03 pts, -0.03 ± 0.04 goals a match; v no manager +0.02 ± 0.04, +0.01 ± 0.05.
 */
export const SUB_MARGIN_SE = 1;
/** Goals a side a match in rival league play (1.74 ± 0.07 measured), for the points a goal is worth. */
export const LEAGUE_GOALS = 1.75;

/** A knight's measured worth in a role, in tiers: tier and build values fitted in mixed line-ups, less what
 * tiredness costs. Tiredness only slows a knight, and a keeper's speed (part of his tier) fits to nothing. */
function worth(tier: number, build: number, role: Role, stamina: number): number {
  if (role === 'gk')
    return ((KEEPER_TIER_VALUE[tier] ?? 0) + (KEEPER_BUILD_VALUE[build] ?? 0)) / GOALS_PER_TIER;
  return (
    ((OUTFIELD_TIER_VALUE[tier] ?? 0) + (OUTFIELD_BUILD_VALUE[build] ?? 0)) / GOALS_PER_TIER -
    tiredCost(stamina)
  );
}

/** The uncertainty of a swap's measured gain, in tiers: one standard error for each fitted value that differs. */
function swapNoise(off: OnPitch, on: KnightSpec): number {
  const differing = Number(off.tier !== on.tier) + Number(off.build !== on.look.build);
  return (VALUE_SE / GOALS_PER_TIER) * Math.sqrt(differing);
}

const poissonPmf = (rate: number, n: number): number[] => {
  const pmf = [Math.exp(-rate)];
  for (let k = 1; k <= n; k++) pmf.push(((pmf[k - 1] ?? 0) * rate) / k);
  return pmf;
};

/** Enough goals that the Poisson tail left out is negligible at any rate this game reaches. */
const MAX_GOALS = 30;

/** The chance the rest of a match ends `diff` goals better for one of two equal sides, `rate` goals each. */
function goalDiffChance(rate: number, diff: number): number {
  const pmf = poissonPmf(rate, MAX_GOALS);
  return pmf.reduce((sum, p, x) => sum + p * (pmf[x - diff] ?? 0), 0);
}

/**
 * League points one more goal of difference is worth with `lead` and `left` of the match to go: half
 * scored (from one behind it earns a draw, from level a win), half not conceded (from level it saves a
 * draw, from one ahead a win). Late on, level is worth most and a settled match next to nothing.
 */
export function pointsPerGoal(lead: number, left: number): number {
  const ends = (final: number): number => goalDiffChance(LEAGUE_GOALS * clamp(left, 0, 1), final - lead);
  return 0.5 * ends(-1) + 1.5 * ends(0) + ends(1);
}

/** What strength now is worth next to the same strength in a future match, which starts level. */
export const leverage = (lead: number, left: number): number =>
  pointsPerGoal(lead, left) / pointsPerGoal(0, 1);

/** A substitution's worth in tiers: the measured gain for the rest of the match (less its uncertainty),
 * weighed by the game state, plus the stamina the starter saves, less what the bench knight spends. */
function subValue(off: OnPitch, on: BenchOption, left: number, lead: number, weight: number): number {
  const gain =
    worth(on.spec.tier, on.spec.look.build, off.role, on.stamina) -
    worth(off.tier, off.build, off.role, off.stamina) -
    SUB_MARGIN_SE * swapNoise(off, on.spec);
  const now = leverage(lead, left) * left * gain;
  const saved = tiredCost(off.stamina - PLAY_COST) - tiredCost(off.stamina - PLAY_COST * (1 - left));
  const spent = tiredCost(on.stamina - PLAY_COST * left) - tiredCost(on.stamina + REST_GAIN);
  return now + weight * (saved - spent);
}

/** The keeper only comes off to rest: tactical subs for him measured -0.31 ± 0.28 goals (rivals.sim, n 49),
 * as the starter the outfield misses least is in goal already; resting subs only, -0.03 ± 0.18 (n 136). */
const mayComeOff = (knight: OnPitch): boolean => knight.role !== 'gk' || knight.stamina < FRESH;

/** A rival's touchline: one substitution when it helps, then tactics late on. At most one action a poll. */
export function createRivalManager(setup: RivalSetup): RivalManager {
  if (setup.starters.length !== STARTERS || !setup.starters.every(Number.isFinite))
    throw new Error(`A rival manager needs the stamina of all ${STARTERS} starters`);
  const weight = futureWeight(setup.style, setup.matchdaysLeft);
  let polls = 0;
  let pitch: readonly OnPitch[] | null = null;
  let made: RivalSub | null = null;
  let tactics = setup.style.tactics;

  /** The pair worth the most, if any is worth making; earlier slots win ties. */
  function bestSub(on: readonly OnPitch[], left: number, lead: number): Omit<RivalSub, 'step'> | null {
    let best: Omit<RivalSub, 'step'> | null = null;
    let bestValue = 0;
    for (const [offSlot, off] of on.entries()) {
      if (!mayComeOff(off)) continue;
      for (const [benchSlot, option] of setup.bench.entries()) {
        const value = subValue(off, option, left, lead, weight);
        if (value > bestValue) [best, bestValue] = [{ offSlot, benchSlot }, value];
      }
    }
    return best;
  }

  const manage: Manager = (state, team): ManagerAction | null => {
    const step = polls++ * POLL_STEPS;
    pitch ??= state.knights
      .filter((k) => k.team === team)
      .map((k, i) => ({
        id: k.id,
        tier: k.tier,
        build: k.look.build,
        role: k.role,
        stamina: setup.starters[i] ?? STAMINA_MAX,
      }));
    const gone = clockGone(state);
    const lead = leadOf(state, team);
    if (!made && gone >= (lead < 0 ? CHASING_SUB_AT : SUB_AT)) {
      const sub = bestSub(pitch, 1 - gone, lead);
      const off = sub && pitch[sub.offSlot];
      const on = sub && setup.bench[sub.benchSlot];
      if (sub && off && on) {
        made = { offSlot: sub.offSlot, benchSlot: sub.benchSlot, step };
        return { kind: 'substitution', offId: off.id, on: on.spec };
      }
    }
    const wanted = rivalTactics(setup.style, gone, lead);
    if (sameTactics(wanted, tactics)) return null;
    tactics = wanted;
    return { kind: 'tactics', tactics };
  };

  return { manage, substitution: () => made };
}
